import {
    RESET_PRODUCT,
    RESET_RACE,
    SELECT_PRODUCT,
    SELECT_PRODUCT_SHORTCUT,
    SELECT_RACE,
    SELECT_RACE_DAY,
} from 'features/AISDataProvider/actions';
import { getIn } from 'immutable';
import Track from 'common/DataObjects/Track';
import { EVENT_PATH, history } from 'utils/navigation';
import raceDaySelector from 'common/selectors/raceDaySelector';
import productSelector from 'common/selectors/productSelector';
import { DEFAULT_SELECTED_PRODUCT } from 'configs/products';
import { SET_DATE } from 'features/DatePicker/actions';
import moment from 'moment';
import { getRacesOpenForSale } from 'common/selectors/racesSelector';

const datePath = ['AISDataProvider', 'selectedRaceDay', 'date'];
const trackPath = ['AISDataProvider', 'selectedRaceDay', 'track', 'name'];

const dateAccessor = (action, state, url) => ({
    date: url.date || raceDaySelector(state).date,
});

const trackAccessor = (action, state, url) => ({
    track: url.track || Track.getSlug(raceDaySelector(state).track.name || ''),
});

const raceAccessor = (action, state, url) => {
    const urlValue = parseInt(url.race, 10);
    const enabledRaces = getRacesOpenForSale(state);
    return {
        race:
            !isNaN(urlValue) && enabledRaces[urlValue - 1] !== undefined
                ? urlValue
                : state.AISDataProvider.raceIndex + 1,
    };
};

const productAccessor = (action, state, url) => ({
    product: url.product || productSelector(state).name,
});

export default {
    // '(/$|/heste|/heste/hjem|/heste/spil)'
    '(/$|/hjem|/spil)': {
        // date is a part of the location.pathname / router parameter
        AIS: {
            // base path that will be added instead of / in this case. Not required.
            basePath: EVENT_PATH['DK'],
            // precondition allows to skip the side effects if it returns false
            precondition: (state, url) =>
                !(
                    history.location.pathname === '/' &&
                    state.AISDataProvider.initializing
                ),
            // action parameter determines specific action to trigger history.push
            // accessor determines the function that will be called in order to
            // return the value that must be pushed as a URL parameter
            // eg. /bets/:date will be replaced with /bets/${action.payload.date}
            actions: [
                {
                    type: SELECT_RACE_DAY,
                    // keys must be the same as described in [[pathnameSchema]]
                    accessor: (action, state, url) => {
                        return {
                            date: action.payload
                                ? action.payload.date
                                : getIn(state, datePath, null),
                            track:
                                action.payload && action.payload.track
                                    ? Track.getSlug(action.payload.track.name)
                                    : Track.getSlug(
                                          getIn(state, trackPath, '')
                                      ),
                            ...productAccessor(action, state, url),
                            ...raceAccessor(action, state, url),
                        };
                    },
                },
                {
                    type: SELECT_PRODUCT,
                    // keys must be the same as described in [[pathnameSchema]]
                    accessor: (action, state, url) => ({
                        ...dateAccessor(action, state, url),
                        ...trackAccessor(action, state, url),
                        product: action.payload.name,
                        ...raceAccessor(action, state, url),
                    }),
                },
                {
                    type: SELECT_PRODUCT_SHORTCUT,
                    // keys must be the same as described in [[pathnameSchema]]
                    accessor: (action, state, url) => ({
                        date: raceDaySelector(state).date,
                        track: Track.getSlug(getIn(state, trackPath, '')),
                        product: action.payload.name,
                        ...raceAccessor(action, state, url),
                    }),
                },
                {
                    type: SELECT_RACE,
                    accessor: (action, state, url) => ({
                        ...dateAccessor(action, state, url),
                        ...trackAccessor(action, state, url),
                        ...productAccessor(action, state, url),
                        race: action.payload.raceIndex + 1,
                    }),
                },
                {
                    type: RESET_RACE,
                    accessor: (action, state, url) => ({
                        ...dateAccessor(action, state, url),
                        ...trackAccessor(action, state, url),
                        ...productAccessor(action, state, url),
                        race: action.payload.raceIndex + 1,
                    }),
                },
                {
                    type: RESET_PRODUCT,
                    accessor: (action, state, url) => ({
                        ...dateAccessor(action, state, url),
                        ...trackAccessor(action, state, url),
                        product: DEFAULT_SELECTED_PRODUCT.name,
                        ...raceAccessor(action, state, url),
                    }),
                },
                {
                    type: SET_DATE,
                    accessor: (action, state, url) => ({
                        date: moment(action.payload).format('YYYY-MM-DD'),
                        ...trackAccessor(action, state, url),
                        ...productAccessor(action, state, url),
                        ...raceAccessor(action, state, url),
                    }),
                },
            ],
            pathnameSchema: ':date/:product/:track/:race',
        },
    },
};
